
import { useState, useEffect } from 'react';
import { attemptStorageRepair } from '@/utils/storageChecker';

export interface SafeImageUrlOptions {
  fallback?: string;
  enableRepair?: boolean;
  timeout?: number;
}

/**
 * A hook to safely handle potentially problematic image URLs
 * with automatic repair and fallback functionality
 */
export function useSafeImageUrl(
  originalUrl: string | null | undefined,
  options: SafeImageUrlOptions = {}
) {
  const [safeUrl, setSafeUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wasRepaired, setWasRepaired] = useState(false);
  
  const fallback = options.fallback || '/placeholder.svg';
  const enableRepair = options.enableRepair !== false;
  const timeout = options.timeout || 5000;
  
  useEffect(() => {
    let isMounted = true;
    setError(null);
    
    // Handle empty URLs
    if (!originalUrl) {
      setSafeUrl(fallback);
      return;
    }
    
    // Skip processing for data URLs or URLs that don't need fixing
    if (
      originalUrl.startsWith('data:') || 
      originalUrl.startsWith('blob:') ||
      originalUrl.startsWith('/') ||  // Local relative path
      !/^https?:\/\/.*?\.(png|jpg|jpeg|webp|gif|svg)/.test(originalUrl)
    ) {
      setSafeUrl(originalUrl);
      return;
    }
    
    const processUrl = async () => {
      // Start with the original URL
      setSafeUrl(originalUrl);
      
      if (enableRepair) {
        setIsLoading(true);
        
        try {
          // Try to repair the URL (add cache busting or get fresh signed URL)
          const timeoutPromise = new Promise<null>((resolve) => {
            setTimeout(() => resolve(null), timeout);
          });
          
          const repairPromise = attemptStorageRepair(originalUrl);
          
          // Race between repair and timeout
          const result = await Promise.race([repairPromise, timeoutPromise]);
          
          if (isMounted) {
            if (result) {
              setSafeUrl(result);
              setWasRepaired(result !== originalUrl);
              setError(null);
            }
            setIsLoading(false);
          }
        } catch (err) {
          if (isMounted) {
            console.error('Error repairing image URL:', err);
            setError(err instanceof Error ? err.message : String(err));
            setIsLoading(false);
          }
        }
      }
    };
    
    processUrl();
    
    return () => {
      isMounted = false;
    };
  }, [originalUrl, fallback, enableRepair, timeout]);
  
  // Verify the image actually loads
  useEffect(() => {
    if (!safeUrl || safeUrl === fallback) return;
    
    const img = new Image();
    let timeoutId: number;
    
    img.onload = () => {
      clearTimeout(timeoutId);
      // URL is good, no need to do anything
    };
    
    img.onerror = () => {
      clearTimeout(timeoutId);
      if (safeUrl !== fallback) {
        console.warn(`Image failed to load: ${safeUrl}`);
        setSafeUrl(fallback);
        setError('Image failed to load');
      }
    };
    
    timeoutId = window.setTimeout(() => {
      console.warn(`Image load timeout: ${safeUrl}`);
      if (safeUrl !== fallback) {
        setSafeUrl(fallback);
        setError('Image load timeout');
      }
    }, timeout);
    
    img.src = safeUrl;
    
    return () => {
      clearTimeout(timeoutId);
      img.onload = null;
      img.onerror = null;
    };
  }, [safeUrl, fallback, timeout]);
  
  return {
    safeUrl,
    isLoading,
    error,
    wasRepaired
  };
}
